import {
  TOpportunitiesV3Details,
  TOpportunitiesV3SupportListParams,
  TOpportunitiesV3UnlinkRequestPayload,
  TOpportunitiesV3OverdueContractItem,
  TOpportunitiesV3ApnefItem,
  TOpportunitiesV3PartnershipItem,
  TOpportunitiesV3Item,
  TOpportunitiesV3ListParams,
  TOpportunitiesV3EvaluationPayload,
  TOpportunitiesV3DetailsResponse,
  TOpportunitiesV3FiltersResponse,
  TOpportunitiesV3ListApiItem,
  TOpportunitiesV3SummaryResponse,
  OPPORTUNITY_TYPE_ENUM
} from './types'
import client from '../../client'
import { ResponseSuccess } from '../../types'

const buildSummaryParams = (params?: TOpportunitiesV3ListParams) => {
  if (!params) return {}

  const {
    search,
    priority,
    offer,
    public_rating,
    fidelity,
    segment,
    network,
    subsidiary,
    user_std_code
  } = params

  return {
    search: search ?? undefined,
    priority: Array.isArray(priority) && priority.length ? priority : undefined,
    offer: Array.isArray(offer) && offer.length ? offer : undefined,
    public_rating: Array.isArray(public_rating) && public_rating.length ? public_rating : undefined,
    fidelity: Array.isArray(fidelity) && fidelity.length ? fidelity : undefined,
    segment: Array.isArray(segment) && segment.length ? segment : undefined,
    network: Array.isArray(network) && network.length ? network : undefined,
    subsidiary: Array.isArray(subsidiary) && subsidiary.length ? subsidiary : undefined,
    user_std_code: Array.isArray(user_std_code) && user_std_code.length ? user_std_code : undefined
  }
}

export const getOpportunitiesSummary = async (params?: TOpportunitiesV3ListParams) => {
  return await client.get<TOpportunitiesV3SummaryResponse>('v3/opportunities/summary', {
    params: buildSummaryParams(params)
  })
}

const splitSegmentName = (segment?: string) => {
  if (!segment) return { segmentName: undefined, subsegmentName: undefined }

  const separator = segment.includes(' / ') ? ' / ' : '/'

  const [segmentName, subsegmentName] = segment.split(separator).map((item) => item.trim())

  return {
    segmentName: segmentName || undefined,
    subsegmentName: subsegmentName || undefined
  }
}

const mapOpportunitiesDetailsResponse = (
  details: TOpportunitiesV3DetailsResponse
): TOpportunitiesV3Details => {
  const { segmentName, subsegmentName } = splitSegmentName(details.header.segment_name)

  const yearMonth = Number(details.header.reference_month)

  const rawTabData = details.store_data

  return {
    type: details.type,
    store: {
      store_name: details.header.store_name,
      tab_number: details.header.tab_number ?? '',
      segment_name: segmentName,
      subsegment_name: subsegmentName,
      store_cnpj_number: details.header.store_cnpj
    },
    potential_value: details.header.opportunity_potential_value,
    tab_data: rawTabData
      ? {
          status: rawTabData.status,
          accreditation_date: rawTabData.accreditation_date ?? '--',
          last_contract_date: rawTabData.last_contract_date ?? '--',
          fantasy_name: rawTabData.fantasy_name ?? '--',
          cnpj: rawTabData.cnpj ?? '--',
          phone: rawTabData.phone ?? '--',
          website: rawTabData.website ?? '--',
          email: rawTabData.email ?? '--',
          address: rawTabData.address ?? '--',
          google_maps_link: rawTabData.google_maps_link
        }
      : undefined,
    has_unlink_request: !details.actions?.can_exclude_opportunity,
    evaluation_expired: details.actions?.evaluation_expired ?? false,
    year_month: Number.isFinite(yearMonth) ? yearMonth : 0,
    priorities:
      details.priorities?.map((priority) => ({
        id: String(priority.id),
        type: priority.description,
        max_percentage: priority.max_percentage,
        percentage: priority.percentage,
        value: priority.value,
        diagnosis: priority.diagnosis
      })) ?? [],
    recommendations:
      details.recommendations?.atendimento?.map((item, index) => ({
        id: `recommendation-${index + 1}`,
        title: item.title,
        description: item.description ?? ''
      })) ?? [],
    stay_tuned:
      details.recommendations?.fique_ligado?.map((item, index) => ({
        id: `stay-tuned-${index + 1}`,
        title: item.title,
        icon: item.icon
      })) ?? [],
    indicators: details.indicators
  }
}

export const getOpportunitiesDetails = async (id: string) => {
  const response = await client.get<ResponseSuccess<TOpportunitiesV3DetailsResponse>>(
    `v3/opportunities/${id}`
  )

  const mappedDetails = mapOpportunitiesDetailsResponse(response.data.data)

  return {
    ...response,
    data: {
      ...response.data,
      data: mappedDetails
    }
  }
}

export const getOpportunitiesOverdueContracts = async (
  params: TOpportunitiesV3SupportListParams
) => {
  const response = await client.get<
    ResponseSuccess<{ contracts?: TOpportunitiesV3OverdueContractItem[] }>
  >(`v3/opportunities/${params.opportunity_id}/support-lists`, {
    params: { list_type: 'contracts', page: params.page, per_page: params.per_page }
  })

  return {
    ...response,
    data: {
      ...response.data,
      data: response.data.data?.contracts ?? []
    }
  }
}

export const getOpportunitiesApnefList = async (params: TOpportunitiesV3SupportListParams) => {
  const response = await client.get<ResponseSuccess<{ apnef?: TOpportunitiesV3ApnefItem[] }>>(
    `v3/opportunities/${params.opportunity_id}/support-lists`,
    {
      params: { list_type: 'apnef', page: params.page, per_page: params.per_page }
    }
  )

  return {
    ...response,
    data: {
      ...response.data,
      data: response.data.data?.apnef ?? []
    }
  }
}

export const getOpportunitiesPartnershipList = async (
  params: TOpportunitiesV3SupportListParams
) => {
  const response = await client.get<
    ResponseSuccess<{ parceria_certa?: TOpportunitiesV3PartnershipItem[] }>
  >(`v3/opportunities/${params.opportunity_id}/support-lists`, {
    params: { list_type: 'parceria_certa', page: params.page, per_page: params.per_page }
  })

  return {
    ...response,
    data: {
      ...response.data,
      data: response.data.data?.parceria_certa ?? []
    }
  }
}

export const createOpportunitiesUnlinkRequest = async (
  data: TOpportunitiesV3UnlinkRequestPayload
) =>
  await client.post<ResponseSuccess<Record<string, unknown>>>(
    'v2/opportunities/unlink-requests',
    data
  )

export const createOpportunitiesEvaluation = async (data: TOpportunitiesV3EvaluationPayload) =>
  await client.post<ResponseSuccess<Record<string, unknown>>>('v3/opportunities/evaluate', data)

export const getOpportunitiesFilters = async () => {
  const response = await client.get<ResponseSuccess<TOpportunitiesV3FiltersResponse>>(
    'v3/opportunities/filters'
  )

  return response
}

const buildListParams = (params?: TOpportunitiesV3ListParams) => {
  const base: Record<string, any> = {
    per_page: params?.per_page ?? 10,
    page: params?.page ?? 1,
    search: params?.search ?? undefined,
    order_by: params?.order_by ?? undefined,
    order_by_direction: params?.order_by_direction ?? undefined
  }

  const multiKeys: (keyof TOpportunitiesV3ListParams)[] = [
    'priority',
    'offer',
    'public_rating',
    'fidelity',
    'segment',
    'network',
    'subsidiary',
    'user_std_code'
  ]

  multiKeys.forEach((key) => {
    const value = params?.[key]

    if (Array.isArray(value) && value.length) {
      base[key] = value
    }
  })

  return base
}

const mapListType = (type?: number) =>
  type === 2 ? OPPORTUNITY_TYPE_ENUM.ACTIVE_BASE : OPPORTUNITY_TYPE_ENUM.ODC

const serializeOpportunitiesV3ListItem = (
  item: TOpportunitiesV3ListApiItem
): TOpportunitiesV3Item => {
  const rawTabNumber = Number(item.store?.tab_number)

  const tabNumber = Number.isFinite(rawTabNumber) ? rawTabNumber : 0

  return {
    id: item.id,
    type: mapListType(item.type),
    store_name: item.store?.name_fantasy ?? '--',
    tab_number: tabNumber,
    store_cnpj: item.store?.cnpj,
    classification: item.classification,
    public_rating: {
      name: item.public_rating,
      bg_color: item.public_rating_bg_color
    },
    priorities: item.priorities?.map((priority) => ({
      order: priority.level,
      type: priority.description,
      value: priority.value
    })),
    description: item.metrics?.potential_value
      ? {
          title: 'Potencial',
          value: item.metrics.potential_value
        }
      : undefined
  }
}

export const getOpportunitiesList = async (params?: TOpportunitiesV3ListParams) => {
  const response = await client.get<ResponseSuccess<TOpportunitiesV3ListApiItem[]>>(
    'v3/opportunities',
    {
      params: buildListParams(params)
    }
  )

  const items = response.data.data

  return {
    ...response,
    data: {
      ...response.data,
      data: Array.isArray(items) ? items.map(serializeOpportunitiesV3ListItem) : []
    }
  }
}
